import React, { useState } from 'react';
import { ChevronLeft, Share2, ImageIcon, Camera, FileText, Trash2, Ban } from 'lucide-react';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import ImagePreviewModal from '../ui/ImagePreviewModal';
import ImageGallery from '../ui/ImageGallery';
import ImageUploader from '../ui/ImageUploader';
import { getStudentName, getCourseName, formatHours } from '../../utils/helpers';
import { PermissionGuard } from '../auth/PermissionGuard';
import { ButtonPermissions } from '../../types/permission';

const RecordDetailView = ({ record, editMode, onBack, onEdit, onSave, onDelete, onShare }) => {
  const [previewImage, setPreviewImage] = useState(null);
  const [sessionArtworkPhotos, setSessionArtworkPhotos] = useState(record?.session_artwork_photos_urls || []);
  const [classroomPhotos, setClassroomPhotos] = useState(record?.classroom_photos_urls || []);
  
  if (!record) return null;

  const totalImages = 
    (sessionArtworkPhotos?.length || 0) + 
    (classroomPhotos?.length || 0);

  const renderImageContent = () => {
    // 编辑模式：使用 ImageUploader
    if (editMode) {
      return (
        <>
          {/* 作品图片上传 */}
          <div>
            <div className="flex justify-between items-center mb-2 px-1">
              <label className="text-sm font-medium text-[#4A5568] flex items-center gap-2">
                <ImageIcon size={16} className="text-[#3182CE]" />
                作品图片
              </label>
            </div>
            <div className="bg-white p-4 rounded-xl border border-[#EDF2F7] shadow-sm">
              <ImageUploader
                value={sessionArtworkPhotos}
                onChange={setSessionArtworkPhotos}
                maxFiles={5}
                uploadMeta={{
                  imageType: '作品',
                  studentId: String(record.student_id),
                  studentName: record.student_name,
                  courseId: String(record.course_id),
                  courseName: record.course_name,
                  teacherName: record.teacher_name
                }}
              />
            </div>
          </div>

          {/* 课堂照片上传 */}
          <div>
            <div className="flex justify-between items-center mb-2 px-1">
              <label className="text-sm font-medium text-[#4A5568] flex items-center gap-2">
                <Camera size={16} className="text-[#3182CE]" />
                课堂照片
              </label>
            </div>
            <div className="bg-white p-4 rounded-xl border border-[#EDF2F7] shadow-sm">
              <ImageUploader
                value={classroomPhotos}
                onChange={setClassroomPhotos}
                maxFiles={10}
                uploadMeta={{
                  imageType: '课堂',
                  studentId: String(record.student_id),
                  studentName: record.student_name,
                  courseId: String(record.course_id),
                  courseName: record.course_name,
                  teacherName: record.teacher_name
                }}
              />
            </div>
          </div>
        </>
      );
    }

    // 查看模式：使用 ImageGallery
    return (
      <ImageGallery
        artworkPhotos={sessionArtworkPhotos || []}
        classroomPhotos={classroomPhotos || []}
      />
    );
  };

  return (
    <div className="flex flex-col h-full bg-[#F7FAFC]">
      <header className="h-[56px] px-4 bg-white flex items-center justify-between border-b border-[#EDF2F7] flex-shrink-0">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="p-1 -ml-1 text-[#2D3748]">
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-[#2D3748]">{editMode ? '编辑记录' : (record.student_name || '未知学员')}</h1>
          {record.status === 'VOIDED' && (
            <Badge color="red" className="flex items-center gap-1">
              <Ban size={12} />
              已作废
            </Badge>
          )}
        </div>
        <Button variant="icon" icon={Share2} onClick={() => onShare && onShare(record)} />
      </header>

      <main className="flex-1 overflow-y-auto p-4">
        <section className="mb-6 bg-white p-4 rounded-xl border border-[#EDF2F7] shadow-sm">
          <h3 className="font-bold text-[#2D3748] mb-4 border-b border-gray-100 pb-2">课程信息</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#718096] block mb-1">学生姓名</label>
              <div className="text-sm font-medium text-[#2D3748]">{record.student_name || '未知学员'}</div>
            </div>
            <div>
              <label className="text-xs text-[#718096] block mb-1">课程名称</label>
              <div className="text-sm font-medium text-[#2D3748]">{record.course_name || '未知课程'}</div>
            </div>
            <div>
              <label className="text-xs text-[#718096] block mb-1">本次消耗</label>
              <div className="text-sm font-medium text-[#2D3748]">{formatHours(record.consumed_hours || 0)} 课时</div>
            </div>
            <div>
              <label className="text-xs text-[#718096] block mb-1">剩余课时</label>
              <div className="text-xl font-bold text-[#3182CE]">{editMode ? <span className="border-b border-gray-300 px-2">{formatHours(record.remaining_hours || 0)}</span> : formatHours(record.remaining_hours || 0)}</div>
            </div>
            {record.use_reward_units && (
              <div>
                <label className="text-xs text-[#718096] block mb-1">课时来源</label>
                <div className="text-sm font-medium text-[#3182CE]">
                  赠送课时
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="mb-6 space-y-4">
          {renderImageContent()}
        </section>

        <section className="mb-4">
          <h3 className="font-bold text-[#2D3748] mb-2 px-1 flex items-center gap-2">
            <FileText size={18} className="text-[#3182CE]" /> 
            {editMode ? '填写评价' : '作品评价'}
          </h3>
          <div className="bg-white p-4 rounded-xl border border-[#EDF2F7] shadow-sm">
            {editMode ? (
              <textarea 
                className="w-full h-32 text-sm text-[#4A5568] focus:outline-none resize-none"
                placeholder="请输入对学生本次表现的评价..."
                defaultValue={record.remarks}
              />
            ) : (
              <p className="text-sm text-[#4A5568] leading-relaxed">
                {record.remarks || "暂无评价内容"}
              </p>
            )}
          </div>
        </section>
      </main>

      <div className="h-[80px] bg-white border-t border-[#EDF2F7] px-6 flex items-center justify-between flex-shrink-0 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.02)] z-10">
        {editMode ? (
          <>
            <Button variant="danger" className="w-24" onClick={onDelete}>作废</Button>
            <Button variant="secondary" onClick={onBack}>返回</Button>
            <Button 
              variant="primary" 
              className="w-24" 
              onClick={() => onSave({
                session_artwork_photos_urls: sessionArtworkPhotos,
                classroom_photos_urls: classroomPhotos
              })}
            >
              保存
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onBack} className={record.status === 'VOIDED' ? 'flex-1' : 'flex-1 mr-4'}>返回</Button>
            {record.status !== 'VOIDED' && (
              <PermissionGuard permission={ButtonPermissions.attendance.edit}>
                <Button variant="primary" className="flex-1" onClick={onEdit}>修改</Button>
              </PermissionGuard>
            )}
          </>
        )}
      </div>

      {previewImage && (
        <ImagePreviewModal
          imageUrl={previewImage.url}
          altText={previewImage.alt}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </div>
  );
};

export default RecordDetailView;
